import {
  CameraIntegration,
  contactSupportMessage,
  formatTitle,
} from './base';
import Bowser from 'bowser';
import dayjs from 'dayjs';
import { post } from '../../helpers/securityService';
import axios from 'axios';
import { loadCameraPending } from './index';

export class RemoteAlly extends CameraIntegration {
  constructor(...args) {
    super(...args);
    this.cameraProviderName = 'remoteally';
    this.videoCache = new Map();
    this.getBrowserAndOS();
  }

  checkForCredentials() {
    this.isAuthenticated =
      !!this.camera?.accessToken &&
      this.camera?.accessToken !== 'refresh_token_invalid' &&
      this.camera?.accessToken !== 'refresh_token_not_available' &&
      this.camera?.accessToken !== 'remoteally_location_access_denied';
    this.camera.authorized = this.isAuthenticated;
  }

  getBrowserAndOS() {
    // const deviceInfo = Bowser.getParser(window.navigator.userAgent);
    const { browser, os } = Bowser.parse(window.navigator.userAgent);
    this.browserName = browser.name.toLowerCase();
    this.osName = os.name.toLowerCase();
  }

  async submitCredentials(username, password) {
    const { location } = this.camera;
    const loginCredentials = {
      un: username,
      pw: password,
    };

    const response = await post(
      `security/remote_ally_submit_creds/${location}`,
      loginCredentials,
      { orgId: this.orgId },
    ).catch((err) => {
      console.error(err);
      throw Error(
        'We were unable to authenticate your Remote Ally account.',
      );
    });

    if (response && response === 'Error fetching new video obj after authentication with Remote Ally') {
      throw Error(
        'Failed to authenticate your Remote Ally account. Please verify the entered credentials.',
      );
    }
    if ( response && response.accessToken === 'remoteally_location_access_denied' ) {
      throw Error(
        'Access to the cameras associated with this location has been denied. Please ensure that your Remote Ally account has the necessary permissions to access this location.',
      );
    }

    this.camera.accessToken = response.accessToken;
    this.camera.locationDetails = response.locationDetails;

    // The backend has now stored the access token in redis but the response
    // does not include cameraList/apiHost/timezone/commonSettings. Re-fetch
    // the full camera payload so fetchThumbnails/fetchVideo have what they
    // need. Flip isAuthenticated only after the refresh, otherwise Vue will
    // mount ThumbnailSlider against an incomplete camera and thumbnails
    // fail on first login.
    const refreshed = await loadCameraPending(this.camera.location, this.orgId).catch((err) => {
      console.error('Failed to refresh Remote Ally camera after authentication:', err);
      return null;
    });
    if (refreshed && typeof refreshed === 'object') {
      Object.assign(this.camera, refreshed);
    }

    this.isAuthenticated = true;
    this.camera.authorized = true;
  }

   async fetchThumbnails() {
    const eventDate = dayjs(this.event.time, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD');
    const eventTime = dayjs(this.event.time, 'YYYY-MM-DD HH:mm:ss').format('HH:mm:ss');

    const { cameraList, apiHost, accessToken } = this.camera;
    const params = {
      date: eventDate,
      time: eventTime,
      accessToken: this.camera.accessToken,
    };

    const thumbnailPromises = cameraList.map(async (camera) => {
      const url = `${apiHost}/cameras/${camera.id}/image?${new URLSearchParams(params).toString()}`;

      const response = await axios.get(url, { responseType: 'blob' });
      const title = formatTitle(camera.name);
      const imageUrl = URL.createObjectURL(new Blob([response.data]));
      return { src: imageUrl, title, id: camera.id };
    });

    const results = await Promise.allSettled(thumbnailPromises);
    const thumbnails = [];
    results.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        thumbnails.push(result.value);
      } else {
        const failedCamera = cameraList[i];
        console.error(`Failed to fetch thumbnail for camera ${failedCamera?.id}:`, result.reason);
        thumbnails.push({
          src: null,
          title: formatTitle(failedCamera?.name || ''),
          id: failedCamera?.id,
        });
      }
    });

    return thumbnails;
  }

  async fetchUrl(url) {
    let firstTsFileEntry = '';

    const { accessToken } = this.camera;

    const headers = {
      Authorization: `Bearer ${accessToken}`,
      'Content-Type': 'application/x-www-form-urlencoded',
    };

    // const response = await axios.get(url, { headers }).catch(err => {
    //   console.error(err);
    //   throw Error(`We were unable to find the video associated with this event. Please try again later. If this error is reoccurring, ${contactSupportMessage}`);
    // });
    const response = await fetch(url, { headers }).catch((err) => {
      console.error(err);
      throw Error(
        `We were unable to find the video associated with this event.  If this error is reoccurring, ${contactSupportMessage}`,
      );
    });

    // Check if response is not OK (status code outside 200-299 range)
    if (!response.ok) {
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        const errorData = await response.json();
        console.error('API Error Response:', errorData);
        return errorData; // Return the error object so caller can handle it
      }
      throw Error(
        `API request failed with status ${response.status}. ${contactSupportMessage}`,
      );
    }

    const data = await response.text();

    const dataLines = data.split('\n');

    // Find the first entry in the line array
    // that matches the .ts extension
    // break out of the loop once entry is found
    try {
      dataLines.forEach((line) => {
        if (/\.ts$/.test(line)) {
          firstTsFileEntry = line;
          // eslint-disable-next-line no-throw-literal
          throw 'Break';
        }
      });
    } catch (e) {
      if (e !== 'Break') throw e;
    }

    return url;
  }

  async fetchVideoBlob(url, accessToken, onProgress) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 45000); // 45 second timeout

    try {
      const response = await fetch(url, {
        signal: controller.signal,
        headers: {
          'Authorization': `Bearer ${accessToken}`
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`Video fetch failed with status ${response.status}`);
      }

      const contentLength = response.headers.get('content-length');
      const total = contentLength ? parseInt(contentLength, 10) : 0;

      if (!total || !onProgress) {
        return await response.blob();
      }

      let loaded = 0;
      const reader = response.body.getReader();
      const chunks = [];

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        loaded += value.length;

        if (onProgress) {
          onProgress(loaded, total);
        }
      }

      return new Blob(chunks);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        throw new Error('Video request timed out. Please try again.');
      }
      console.error('Error fetching video blob:', error);
      throw new Error(`We were unable to fetch the video. ${contactSupportMessage}`);
    }
  }

  async fetchVideo(cameraId, seekTime) {
      const { timezone, commonSettings, cameraList, accessToken, apiHost } =
        this.camera;

      const { time } = this.event;
      const nowPlaying = {
        seekButtons: true,
        offsetSeconds: 0,
        displayTime: dayjs(time, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD HH:mm:ss'),
      };

      // If video seek button pressed
      if (seekTime) {
        nowPlaying.offsetSeconds += seekTime;
        nowPlaying.displayTime = dayjs(
          nowPlaying.displayTime,
          'YYYY-MM-DD HH:mm:ss',
        )
          .add(seekTime, 'second')
          .format('YYYY-MM-DD HH:mm:ss');
        this.event.time = nowPlaying.displayTime;
      }

      if (!cameraList || !cameraList[0]) {
        console.error('Missing cameraList:', cameraList);
        throw Error(
          `We were unable to find the cameras associated with your Remote Ally account. ${contactSupportMessage}`,
        );
      }

      if (cameraId)
        nowPlaying.camera = cameraList.find((cam) => cam.id === cameraId);
      else [nowPlaying.camera] = cameraList;

      if (Object.keys(nowPlaying.camera).length === 0) {
        console.error(
          'Could not find the camera associated with the provided camera id in the camera list',
          { cameraId, cameraList },
        );
        throw Error(
          `We were unable to find the video for this camera because this camera was not found to be associated with your AI Vision Technologies account. ${contactSupportMessage}`,
        );
      }

      const videoDuration = commonSettings?.videoDurationSeconds || 30;
      const videoEndTime = dayjs(nowPlaying.displayTime, 'YYYY-MM-DD HH:mm:ss')
        .add(videoDuration, 'seconds')
        .format('HH:mm:ss');

      nowPlaying.event = this.event;
      nowPlaying.description = `${nowPlaying.camera.name} on ${nowPlaying.displayTime} to ${videoEndTime}`;
      nowPlaying.cameraId = nowPlaying.camera.id;

      if (!nowPlaying.cameraId) {
        console.error(
          'Could not find the camera id from the selected camera needed to update the video player',
          nowPlaying.camera,
        );
        throw Error(
          `We were unable to identify the selected camera in order to display the associated video. ${contactSupportMessage}`,
        );
      }

      const eventDate = dayjs(nowPlaying.displayTime, 'YYYY-MM-DD HH:mm:ss').format('YYYY-MM-DD');
      const eventTime = dayjs(nowPlaying.displayTime, 'YYYY-MM-DD HH:mm:ss').format('HH:mm:ss');
      const params = {
        date: eventDate,
        time: eventTime,
        durationSeconds: videoDuration,
        accessToken: accessToken,
      };

      const apiUrl = `${apiHost}/cameras/${nowPlaying.cameraId}/video?${new URLSearchParams(params).toString()}`;

      // Check cache first
      const cacheKey = `${nowPlaying.cameraId}_${eventDate}_${eventTime}_${videoDuration}`;

      if (this.videoCache.has(cacheKey)) {
        nowPlaying.video = {
          sources: [{ src: this.videoCache.get(cacheKey), type: 'video/mp4' }],
          ...this.commonVideoOptions,
        };
        return nowPlaying;
      }

      // Fetch video blob with progress tracking
      const videoBlob = await this.fetchVideoBlob(apiUrl, accessToken, (loaded, total) => {
        const percent = Math.round((loaded / total) * 100);
        // console.log(`Video download progress: ${percent}%`);
      });

      const videoObjectUrl = URL.createObjectURL(videoBlob);

      // Cache the video object URL
      this.videoCache.set(cacheKey, videoObjectUrl);

      // Clean up old cache entries if cache gets too large (keep last 10 videos)
      if (this.videoCache.size > 10) {
        const firstKey = this.videoCache.keys().next().value;
        const oldUrl = this.videoCache.get(firstKey);
        URL.revokeObjectURL(oldUrl);
        this.videoCache.delete(firstKey);
      }

      nowPlaying.video = {
        sources: [{ src: videoObjectUrl, type: 'video/mp4' }],
        ...this.commonVideoOptions,
      };

      return nowPlaying;
    }

  destroy() {
    // Clean up all cached video object URLs to prevent memory leaks
    for (const url of this.videoCache.values()) {
      URL.revokeObjectURL(url);
    }
    this.videoCache.clear();
  }
}
